/*
 * Scentt filters \u2013 <scentt-filters> filters, counts, sorting and "Show more" for a product grid of up to 250 cards,
 * worked out in the browser from the cards on the page, so the numbers always match what the shopper sees.
 * Markup: snippets/scentt-filters.liquid (bar + panel) and snippets/scentt-product-facts.liquid (data-* on each card).
 * URL state (?sf=brand:gucci,dior~price:10-200&sfsort=price-asc) is read through a strict allow-list.
 */
(() => {
  if (!window.Scentt || customElements.get('scentt-filters')) return;
  const S = window.Scentt && Object.isFrozen(window.Scentt) && typeof window.Scentt.html === 'function' ? window.Scentt : null;

  const KEYS = ['avail', 'brand', 'size', 'family', 'for', 'occasion', 'conc', 'format', 'notes'];
  const LIST_KEYS = KEYS.slice(1);
  const SORTS = ['relevance', 'price-asc', 'price-desc', 'title-asc', 'title-desc', 'date-desc'];
  const LAYOUTS = ['vertical', 'horizontal', 'drawer'];
  const MAX_ITEMS = 250;
  const MAX_LEN = 80;
  const MAX_VALUES = 40;
  const SHOW_VALUES = 8;
  const PRICE_RE = /^(\d{0,7}(?:\.\d{1,2})?)-(\d{0,7}(?:\.\d{1,2})?)$/;
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const DEFAULT_STRINGS = {
    count_one: '1 product', count_other: '[count] products', show_results_one: 'Show 1 result', show_results_other: 'Show [count] results',
    showing: 'Showing [shown] of [total]', show_more: 'Show more', show_all: 'Show all [count]', show_less: 'Show less',
    empty: 'No products match these filters.', clear_all: 'Clear all', clear_filters: 'Clear filters', remove: 'Remove filter: [label]',
    in_stock: 'In stock', out_of_stock: 'Out of stock', price: 'Price', price_min: 'Minimum price', price_max: 'Maximum price', to: 'to',
  };

  /* ---------- small pure helpers ---------- */
  const norm = (v) => String(v == null ? '' : v).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim().slice(0, MAX_LEN);
  const pretty = (v) => {
    const s = String(v || '').trim().slice(0, MAX_LEN);
    if (!s) return s;
    if (/^\d+\s*x/i.test(s) || /^[\d.]+\s*ml/i.test(s)) return s.toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };
  /** Size in ml for numeric sorting ("3x10ml" \u2192 30). Values without a number go last. */
  const ml = (v) => {
    let m = /(\d+)\s*x\s*([\d.]+)/i.exec(v);
    if (m) return parseFloat(m[1]) * parseFloat(m[2]) + 0.01;
    m = /([\d.]+)/.exec(v);
    return m ? parseFloat(m[1]) : 1e9;
  };
  const fill = (tpl, map) => String(tpl).replace(/\[(\w+)\]/g, (all, k) => (k in map ? String(map[k]) : all));
  const parseJSON = (raw, def) => {
    try { const v = JSON.parse(raw || ''); return v == null ? def : v; } catch (e) { return def; }
  };
  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };
  const caret = () => {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', 'scentt-filters__caret');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'm6 9 6 6 6-6');
    svg.append(path);
    return svg;
  };

  class ScenttFilters extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const gridId = String(this.dataset.grid || '');
      this.grid = /^[\w-]{1,120}$/.test(gridId) ? document.getElementById(gridId) : null;
      if (!this.grid) { S.warn('scentt-filters: grid not found'); return; }
      this.ready = true;
      this.readOptions();
      this.readItems();
      this.build();
      this.restore();
      this.bind();
      this.apply(true);
      this.classList.add('is-ready');
      this.grid.classList.add('is-scentt-ready');
    }

    disconnectedCallback() {
      (this.cleanups || []).forEach((fn) => fn());
      this.cleanups = [];
      this.lock(false);
      this.ready = false;
    }

    /* ---------- options (all from data-*, validated) ---------- */
    readOptions() {
      const d = this.dataset;
      this.layout = S.oneOf(d.layout, LAYOUTS, 'vertical');
      this.perPage = S.int(d.perPage, 24, 1, MAX_ITEMS);
      this.openCount = this.layout === 'horizontal' ? 0 : S.int(d.open, 3, 0, 20);
      this.showPrice = d.price !== 'false';
      this.currency = String(d.currency || '').slice(0, 6);
      this.sortDefault = S.oneOf(d.sortDefault, ['featured', 'relevance'], 'relevance');
      /* Theme setting "Show in-stock products first" (off by default): sold-out cards stay last whatever the sort */
      this.stockFirst = d.instockFirst === 'true';
      /* Off inside the compare pop-up: the filters there must not change, or be set by, the page address */
      this.urlState = d.urlState !== 'false';
      const strings = parseJSON(d.strings, {});
      this.t = {};
      Object.keys(DEFAULT_STRINGS).forEach((k) => {
        const v = strings && typeof strings[k] === 'string' ? strings[k] : '';
        this.t[k] = v ? v.slice(0, 200) : DEFAULT_STRINGS[k];
      });
      const groups = parseJSON(d.groups, []);
      const seen = new Set();
      this.groups = (Array.isArray(groups) ? groups : [])
        .filter((g) => g && KEYS.includes(g.key) && !seen.has(g.key) && seen.add(g.key))
        .map((g) => ({ key: g.key, label: (typeof g.label === 'string' && g.label.trim() ? g.label : g.key).slice(0, 60) }));
      const conc = parseJSON(d.concNames, {});
      this.concNames = {};
      if (conc && typeof conc === 'object' && !Array.isArray(conc)) {
        Object.keys(conc).forEach((k) => { if (/^[a-z]{2,12}$/.test(k) && typeof conc[k] === 'string') this.concNames[k] = conc[k].slice(0, MAX_LEN); });
      }
      this.sel = {};
      this.pmin = null;
      this.pmax = null;
      this.sort = 'relevance';
      this.limit = this.perPage;
      this.labels = new Map();
    }

    /* ---------- read the cards (facts written by scentt-product-facts) ---------- */
    readItems() {
      const cards = Array.from(this.grid.children).filter((n) => n.hasAttribute('data-facts')).slice(0, MAX_ITEMS);
      this.items = cards.map((card, i) => {
        const d = card.dataset;
        const it = {
          el: card,
          i,
          avail: d.avail === 'out' ? 'out' : 'in',
          price: S.int(d.price, 0, 0, 1e11),
          title: String(d.title || '').slice(0, 200),
          date: S.int(d.date, 0, 0, 1e11),
        };
        LIST_KEYS.forEach((k) => {
          it[k] = [];
          let raw = String(d[k] || '');
          if (k === 'conc') {
            const key = String(d.concKey || '').trim();
            if (this.concNames[key]) { this.addValue(it, k, key, this.concNames[key]); return; }
          }
          raw = raw.slice(0, 2000);
          /* Values from metafields may be clustered ("Woody, Amber; Musk") \u2013 split them. Brand names keep their commas. */
          raw.split(k === 'brand' ? /\|/ : /[|;,\n]/).slice(0, MAX_VALUES).forEach((v) => this.addValue(it, k, v, v));
        });
        return it;
      });
      const prices = this.items.map((it) => it.price);
      this.priceLo = prices.length ? Math.floor(Math.min(...prices) / 100) : 0;
      this.priceHi = prices.length ? Math.ceil(Math.max(...prices) / 100) : 0;
    }

    addValue(it, key, value, label) {
      const n = norm(value);
      if (!n || it[key].includes(n)) return;
      it[key].push(n);
      const lk = `${key}:${n}`;
      const current = this.labels.get(lk);
      /* Prefer a nicely capitalised label when the same value is written in different ways */
      if (!current || current === current.toLowerCase()) this.labels.set(lk, key === 'conc' && this.concNames[n] ? this.concNames[n] : pretty(label));
    }

    values(key) {
      if (key === 'avail') return ['in', 'out'];
      const set = new Set();
      this.items.forEach((it) => it[key].forEach((v) => set.add(v)));
      const vals = Array.from(set);
      if (key === 'size') vals.sort((a, b) => ml(a) - ml(b) || a.localeCompare(b));
      else if (key === 'conc') {
        const order = Object.keys(this.concNames);
        const rank = (v) => (order.includes(v) ? order.indexOf(v) : 999);
        vals.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
      } else vals.sort((a, b) => this.label(key, a).localeCompare(this.label(key, b)));
      return vals;
    }

    label(key, v) {
      if (key === 'avail') return v === 'in' ? this.t.in_stock : this.t.out_of_stock;
      return this.labels.get(`${key}:${v}`) || pretty(v);
    }

    money(major) {
      /* Whole currency units for the price filter: drop a trailing .00 / ,00 */
      return S.money(Math.round(major * 100)).replace(/[.,]00(?=\D*$)/, '');
    }

    /* ---------- build the groups (DOM only, never innerHTML) ---------- */
    build() {
      const list = this.querySelector('[data-sf-list]');
      if (!list) return;
      const frag = document.createDocumentFragment();
      this.known = {};
      let n = 0;
      const addPrice = () => {
        if (!this.showPrice || this.priceHi <= this.priceLo) return;
        frag.append(this.priceGroup(n < this.openCount));
        n += 1;
      };
      const hasAvail = this.groups.some((g) => g.key === 'avail');
      if (!hasAvail) addPrice();
      this.groups.forEach((g) => {
        const vals = this.values(g.key);
        this.known[g.key] = new Set(vals);
        if (!this.items.length || (g.key !== 'avail' && !vals.length)) return;
        frag.append(this.group(g, vals, n < this.openCount));
        n += 1;
        if (g.key === 'avail') addPrice();
      });
      list.replaceChildren(frag);
    }

    shell(key, label, open) {
      const details = el('details', 'scentt-filters__group');
      details.dataset.sfGroup = key;
      if (open) details.open = true;
      const summary = el('summary', 'scentt-filters__summary');
      const sel = el('span', 'scentt-filters__sel');
      sel.dataset.sfSel = '';
      summary.append(el('span', 'scentt-filters__name', label), sel, caret());
      const drop = el('div', 'scentt-filters__drop');
      details.append(summary, drop);
      return { details, drop };
    }

    group(g, vals, open) {
      const { details, drop } = this.shell(g.key, g.label, open);
      const fieldset = el('fieldset', 'scentt-filters__fieldset');
      fieldset.append(el('legend', 'visually-hidden', g.label));
      const ul = el('ul', 'scentt-filters__vals');
      ul.setAttribute('role', 'list');
      vals.forEach((v, idx) => {
        const li = el('li', idx >= SHOW_VALUES ? 'is-extra' : '');
        const lab = el('label', 'scentt-filters__val');
        const input = el('input');
        input.type = 'checkbox';
        input.value = v;
        input.dataset.sfCheck = g.key;
        const num = el('span', 'scentt-filters__num');
        num.dataset.sfNum = '';
        const box = el('span', 'scentt-filters__box');
        box.setAttribute('aria-hidden', 'true');
        lab.append(input, box, el('span', 'scentt-filters__label', this.label(g.key, v)), num);
        li.append(lab);
        ul.append(li);
      });
      fieldset.append(ul);
      drop.append(fieldset);
      if (vals.length > SHOW_VALUES) {
        const more = el('button', 'scentt-filters__more', fill(this.t.show_all, { count: vals.length }));
        more.type = 'button';
        more.dataset.sfMore = '';
        more.setAttribute('aria-expanded', 'false');
        drop.append(more);
      }
      return details;
    }

    priceGroup(open) {
      const { details, drop } = this.shell('price', this.t.price, open);
      const wrap = el('div', 'scentt-filters__price');
      const field = (attr, label, placeholder) => {
        const lab = el('label', 'scentt-filters__price-field');
        const input = el('input');
        input.type = 'number';
        input.inputMode = 'numeric';
        input.min = '0';
        input.step = '1';
        input.placeholder = String(placeholder);
        input.setAttribute('aria-label', label);
        input.setAttribute(attr, '');
        lab.append(el('span', '', this.currency), input);
        return lab;
      };
      const to = el('span', '', this.t.to);
      to.setAttribute('aria-hidden', 'true');
      wrap.append(field('data-sf-pmin', this.t.price_min, this.priceLo), to, field('data-sf-pmax', this.t.price_max, this.priceHi));
      drop.append(wrap);
      return details;
    }

    /* ---------- filtering ---------- */
    match(it, except) {
      for (const k of Object.keys(this.sel)) {
        if (k === except) continue;
        const set = this.sel[k];
        if (!set || !set.length) continue;
        if (k === 'avail') { if (!set.includes(it.avail)) return false; continue; }
        if (!it[k].some((v) => set.includes(v))) return false;
      }
      if (except !== 'price') {
        if (this.pmin != null && it.price < this.pmin * 100) return false;
        if (this.pmax != null && it.price > this.pmax * 100) return false;
      }
      return true;
    }

    sorted(list) {
      const s = this.sort;
      const stockFirst = this.stockFirst;
      return list.slice().sort((a, b) => {
        if (stockFirst && a.avail !== b.avail) return a.avail === 'in' ? -1 : 1;
        switch (s) {
          case 'price-asc': return a.price - b.price || a.i - b.i;
          case 'price-desc': return b.price - a.price || a.i - b.i;
          case 'title-asc': return a.title.localeCompare(b.title) || a.i - b.i;
          case 'title-desc': return b.title.localeCompare(a.title) || a.i - b.i;
          case 'date-desc': return b.date - a.date || a.i - b.i;
          default: return a.i - b.i;
        }
      });
    }

    /* Turn off the theme's scroll-in animation once the shopper filters or sorts:
       moved cards would otherwise fade in again one by one. */
    calm() {
      if (this.calmed) return;
      this.calmed = true;
      const anim = ['scroll-trigger', 'animate--slide-in', 'animate--fade-in', 'scroll-trigger--offscreen', 'scroll-trigger--cancel'];
      this.items.forEach((it) => {
        it.el.classList.remove(...anim);
        it.el.removeAttribute('data-cascade');
        it.el.style.removeProperty('--animation-order');
      });
      const wrap = this.grid.closest('[data-cascade]');
      if (wrap) { wrap.removeAttribute('data-cascade'); wrap.classList.remove(...anim); }
    }

    apply(initial = false) {
      const shown = this.sorted(this.items.filter((it) => this.match(it, null)));
      const shownSet = new Set(shown);
      const order = shown.concat(this.items.filter((it) => !shownSet.has(it)));

      /* Only move cards when the order really changes, so nothing flickers or re-animates */
      const current = Array.from(this.grid.children).filter((n) => n.hasAttribute('data-facts'));
      const same = current.length === order.length && order.every((it, idx) => current[idx] === it.el);
      if (!same) {
        if (!initial) this.calm();
        const frag = document.createDocumentFragment();
        order.forEach((it) => frag.append(it.el));
        this.grid.append(frag);
      }
      const changed = [];
      order.forEach((it, idx) => {
        const hide = !shownSet.has(it) || idx >= this.limit;
        if (it.el.hidden !== hide) changed.push([it.el, hide]);
      });
      if (!initial && changed.length) this.calm();
      changed.forEach(([node, hide]) => { node.hidden = hide; });

      this.updateGroups();
      this.updateSummary(shown.length);
      if (!initial) {
        this.save();
        S.emit('filters:change', { count: shown.length });
      }
    }

    updateGroups() {
      this.querySelectorAll('[data-sf-group]').forEach((grp) => {
        const key = grp.dataset.sfGroup;
        const sel = grp.querySelector('[data-sf-sel]');
        if (key === 'price') {
          sel.textContent = this.pmin != null || this.pmax != null ? `(${this.priceText()})` : '';
          return;
        }
        const base = this.items.filter((it) => this.match(it, key));
        const counts = new Map();
        base.forEach((it) => {
          if (key === 'avail') counts.set(it.avail, (counts.get(it.avail) || 0) + 1);
          else it[key].forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
        });
        const chosen = this.sel[key] || [];
        grp.querySelectorAll('[data-sf-check]').forEach((cb) => {
          const c = counts.get(cb.value) || 0;
          const li = cb.closest('li');
          li.querySelector('[data-sf-num]').textContent = `(${c})`;
          cb.checked = chosen.includes(cb.value);
          cb.disabled = !c && !cb.checked;
          li.classList.toggle('is-empty', !c && !cb.checked);
        });
        sel.textContent = chosen.length ? `(${chosen.length})` : '';
      });
    }

    priceText() {
      const lo = this.pmin != null ? this.pmin : this.priceLo;
      const hi = this.pmax != null ? this.pmax : this.priceHi;
      return `${this.money(lo)}\u2013${this.money(hi)}`;
    }

    updateSummary(total) {
      const countEl = this.querySelector('[data-sf-count]');
      const text = total === 1 ? this.t.count_one : fill(this.t.count_other, { count: total });
      if (countEl && countEl.textContent !== text) countEl.textContent = text;
      const done = this.querySelector('[data-sf-done]');
      if (done) done.textContent = total === 1 ? this.t.show_results_one : fill(this.t.show_results_other, { count: total });

      /* active filter chips */
      const chips = [];
      const chip = (label, key, value) => {
        const b = el('button', 'scentt-filters__chip');
        b.type = 'button';
        b.dataset.sfRemove = key;
        if (value != null) b.dataset.value = value;
        b.setAttribute('aria-label', fill(this.t.remove, { label }));
        const x = el('span', 'scentt-filters__chip-x', '\u00d7');
        x.setAttribute('aria-hidden', 'true');
        b.append(el('span', '', label), x);
        chips.push(b);
      };
      Object.keys(this.sel).forEach((k) => (this.sel[k] || []).forEach((v) => chip(this.label(k, v), k, v)));
      if (this.pmin != null || this.pmax != null) chip(this.priceText(), 'price', null);
      const active = this.querySelector('[data-sf-active]');
      if (chips.length) {
        const clear = el('button', 'scentt-filters__clear', this.t.clear_all);
        clear.type = 'button';
        clear.dataset.sfClear = '';
        chips.push(clear);
      }
      if (active) { active.replaceChildren(...chips); active.hidden = !chips.length; }
      const badge = this.querySelector('[data-sf-badge]');
      const n = chips.length ? chips.length - 1 : 0;
      if (badge) { badge.textContent = n ? String(n) : ''; badge.hidden = !n; }

      /* "Show more" + empty state live right after the grid */
      const more = this.moreBox();
      const shownNow = Math.min(total, this.limit);
      more.hidden = total <= this.limit;
      more.querySelector('[data-sf-left]').textContent = fill(this.t.showing, { shown: shownNow, total });
      const empty = this.emptyBox();
      empty.hidden = total > 0 || !this.items.length;
    }

    moreBox() {
      if (this.more && this.more.isConnected) return this.more;
      const box = el('div', 'scentt-filters__morewrap');
      box.hidden = true;
      const left = el('p', 'scentt-filters__left');
      left.dataset.sfLeft = '';
      const btn = el('button', 'button button--secondary scentt-filters__showmore', this.t.show_more);
      btn.type = 'button';
      btn.addEventListener('click', () => {
        const first = this.items.filter((it) => !it.el.hidden).length;
        this.limit += this.perPage;
        this.apply();
        /* move focus to the first newly shown card for keyboard users */
        const next = Array.from(this.grid.children).filter((c) => c.hasAttribute('data-facts') && !c.hidden)[first];
        const link = next && next.querySelector('a[href]');
        if (link) link.focus({ preventScroll: true });
      });
      box.append(left, btn);
      this.grid.after(box);
      this.more = box;
      return box;
    }

    emptyBox() {
      if (this.empty && this.empty.isConnected) return this.empty;
      const box = el('div', 'scentt-filters__empty scentt-empty');
      box.hidden = true;
      const btn = el('button', 'button button--secondary', this.t.clear_filters);
      btn.type = 'button';
      btn.addEventListener('click', () => this.clear());
      box.append(el('p', '', this.t.empty), btn);
      this.grid.after(box);
      this.empty = box;
      return box;
    }

    clear() {
      this.sel = {};
      this.pmin = null;
      this.pmax = null;
      this.limit = this.perPage;
      this.querySelectorAll('[data-sf-pmin],[data-sf-pmax]').forEach((i) => { i.value = ''; });
      this.apply();
    }

    /* ---------- URL state (shareable links) ---------- */
    save() {
      if (!this.urlState) return;
      try {
        const parts = [];
        Object.keys(this.sel).forEach((k) => {
          if (this.sel[k].length) parts.push(`${k}:${this.sel[k].map((v) => encodeURIComponent(v).replace(/~/g, '%7E')).join(',')}`);
        });
        if (this.pmin != null || this.pmax != null) parts.push(`price:${this.pmin != null ? this.pmin : ''}-${this.pmax != null ? this.pmax : ''}`);
        const url = new URL(window.location.href);
        if (parts.length) url.searchParams.set('sf', parts.join('~')); else url.searchParams.delete('sf');
        if (this.sort !== 'relevance') url.searchParams.set('sfsort', this.sort); else url.searchParams.delete('sfsort');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      } catch (e) {
        S.warn('scentt-filters: could not update the URL', e);
      }
    }

    /** Strict allow-list: known group keys, values that exist on this page, numeric prices, known sorts. Everything else is ignored. */
    restore() {
      if (!this.urlState) return;
      let params;
      try { params = new URL(window.location.href).searchParams; } catch (e) { return; }
      const sf = String(params.get('sf') || '').slice(0, 2000);
      if (sf) {
        sf.split('~').slice(0, KEYS.length + 1).forEach((part) => {
          const at = part.indexOf(':');
          if (at < 1) return;
          const key = part.slice(0, at);
          const raw = part.slice(at + 1);
          if (key === 'price') {
            if (!this.showPrice) return;
            const m = PRICE_RE.exec(raw);
            if (!m) return;
            const lo = m[1] === '' ? null : Math.max(0, Number(m[1]));
            const hi = m[2] === '' ? null : Math.max(0, Number(m[2]));
            this.pmin = Number.isFinite(lo) ? lo : null;
            this.pmax = Number.isFinite(hi) ? hi : null;
            const a = this.querySelector('[data-sf-pmin]');
            const b = this.querySelector('[data-sf-pmax]');
            if (a && this.pmin != null) a.value = String(this.pmin);
            if (b && this.pmax != null) b.value = String(this.pmax);
            return;
          }
          if (!Object.prototype.hasOwnProperty.call(this.known || {}, key)) return;
          const allowed = this.known[key];
          const vals = [];
          raw.split(',').slice(0, MAX_VALUES).forEach((v) => {
            let dec = '';
            try { dec = decodeURIComponent(v); } catch (e) { return; }
            dec = norm(dec);
            if (allowed.has(dec) && !vals.includes(dec)) vals.push(dec);
          });
          if (vals.length) this.sel[key] = vals;
        });
      }
      const srt = params.get('sfsort');
      if (SORTS.includes(srt)) {
        this.sort = srt;
        const select = this.querySelector('[data-sf-sort]');
        if (select) select.value = srt;
      }
    }

    /* ---------- drawer / dropdowns ---------- */
    isRow() { return this.layout === 'horizontal' && this.desktop.matches; }
    isDrawer() { return this.layout === 'drawer' || !this.desktop.matches; }

    lock(on) { document.documentElement.classList.toggle('scentt-filters-lock', on); }

    toggle(open) {
      const panel = this.querySelector('[data-sf-panel]');
      const opener = this.querySelector('[data-sf-open]');
      this.classList.toggle('is-open', open);
      this.lock(open);
      if (opener) opener.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) {
        if (open) { panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-modal', 'true'); } else { panel.removeAttribute('role'); panel.removeAttribute('aria-modal'); }
      }
      if (open) {
        const close = this.querySelector('[data-sf-close]');
        window.setTimeout(() => (close || panel).focus({ preventScroll: true }), 30);
      } else if (opener && this.contains(document.activeElement)) {
        opener.focus({ preventScroll: true });
      }
    }

    closeDrops(except) {
      this.querySelectorAll('[data-sf-group][open]').forEach((d) => { if (d !== except) d.open = false; });
    }

    bind() {
      this.cleanups = [];
      this.desktop = window.matchMedia('(min-width: 990px)');
      const listen = (target, type, fn, opts) => {
        if (typeof target.addEventListener === 'function') {
          target.addEventListener(type, fn, opts);
          this.cleanups.push(() => target.removeEventListener(type, fn, opts));
        } else if (type === 'change' && typeof target.addListener === 'function') {
          /* iOS 13 and older: MediaQueryList only has addListener */
          target.addListener(fn);
          this.cleanups.push(() => target.removeListener(fn));
        }
      };

      listen(this, 'change', (e) => {
        const t = e.target;
        if (t.matches('[data-sf-check]')) {
          const k = t.dataset.sfCheck;
          if (!this.known[k] || !this.known[k].has(t.value)) return;
          const set = (this.sel[k] = this.sel[k] || []);
          const at = set.indexOf(t.value);
          if (t.checked && at === -1) set.push(t.value);
          if (!t.checked && at > -1) set.splice(at, 1);
          if (!set.length) delete this.sel[k];
          this.limit = this.perPage;
          this.apply();
        } else if (t.matches('[data-sf-pmin],[data-sf-pmax]')) {
          const read = (sel) => {
            const v = this.querySelector(sel).value;
            const n = Number.parseFloat(v);
            return v === '' || !Number.isFinite(n) ? null : Math.min(1e7, Math.max(0, Math.round(n)));
          };
          this.pmin = read('[data-sf-pmin]');
          this.pmax = read('[data-sf-pmax]');
          this.limit = this.perPage;
          this.apply();
        } else if (t.matches('[data-sf-sort]')) {
          this.sort = S.oneOf(t.value, SORTS, 'relevance');
          this.apply();
        }
      });

      listen(this, 'click', (e) => {
        const t = e.target;
        if (!(t instanceof Element)) return;
        if (t.closest('[data-sf-open]')) { this.toggle(true); return; }
        if (t.closest('[data-sf-close]') || t.closest('[data-sf-done]')) { this.toggle(false); return; }
        if (t.closest('[data-sf-clear]')) { this.clear(); return; }
        const rm = t.closest('[data-sf-remove]');
        if (rm) {
          const k = rm.dataset.sfRemove;
          if (k === 'price') {
            this.pmin = null;
            this.pmax = null;
            this.querySelectorAll('[data-sf-pmin],[data-sf-pmax]').forEach((i) => { i.value = ''; });
          } else if (this.sel[k]) {
            this.sel[k] = this.sel[k].filter((v) => v !== rm.dataset.value);
            if (!this.sel[k].length) delete this.sel[k];
          }
          this.apply();
          const next = this.querySelector('[data-sf-remove], [data-sf-clear]');
          if (next && next.offsetParent) next.focus({ preventScroll: true });
          return;
        }
        const more = t.closest('[data-sf-more]');
        if (more) {
          const grp = more.closest('[data-sf-group]');
          const on = grp.classList.toggle('is-expanded');
          const total = grp.querySelectorAll('[data-sf-check]').length;
          more.textContent = on ? this.t.show_less : fill(this.t.show_all, { count: total });
          more.setAttribute('aria-expanded', on ? 'true' : 'false');
        }
      });

      /* Horizontal row: one dropdown open at a time */
      listen(this, 'toggle', (e) => {
        if (this.isRow() && e.target.open && e.target.matches('[data-sf-group]')) this.closeDrops(e.target);
      }, true);

      listen(document, 'keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (this.classList.contains('is-open')) this.toggle(false);
        else if (this.isRow()) {
          const openGroup = this.querySelector('[data-sf-group][open]');
          if (openGroup) { openGroup.open = false; openGroup.querySelector('summary').focus(); }
        }
      });
      listen(document, 'click', (e) => {
        if (!this.isRow() || (e.target instanceof Element && e.target.closest('[data-sf-group]'))) return;
        this.closeDrops(null);
      }, { passive: true });
      listen(this.desktop, 'change', () => {
        if (!this.isDrawer() && this.classList.contains('is-open')) this.toggle(false);
        if (!this.isRow()) return;
        this.closeDrops(null);
      });
    }
  }

  customElements.define('scentt-filters', ScenttFilters);
})();
